import apiClient from "./apiClient";
import { API_ENDPOINTS } from "../config/api";
import type { CreateBatchResponse, UploadUrlsResponse, UploadRequestConfig, UploadProgressEvent, UploadResponse } from "../types/upload";

export const uploadFiles = async (files: File[], notes: string, jobId: number) => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("notes", notes);
    formData.append("job_id", jobId.toString());
    const config: UploadRequestConfig = {
      headers: {},
      timeout: 300000,
      onUploadProgress: (progressEvent: UploadProgressEvent) => {
        if (!progressEvent.total) return;
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        console.log(`Upload Progress: ${percentCompleted}%`);
      },
    };

    const response = await apiClient.post<UploadResponse>(
      "/upload",
      formData,
      config,
    );
    return response.data;
  } catch (error: unknown) {
    if (typeof error === "object" && error !== null && "response" in error) {
      const err = error as {
        response?: {
          status?: number;
          data?: { message?: string; detail?: string };
        };
        message?: string;
      };

      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        `Upload failed: ${err.message ?? "Unknown error"}`;

      console.error("Upload error details:", {
        status: err.response?.status,
        data: err.response?.data,
        message: errorMessage,
      });

      throw new Error(errorMessage);
    }

    if (error instanceof Error) {
      throw new Error(`Upload failed: ${error.message}`);
    }

    throw new Error("Upload failed: Unknown error");
  }
};

export const uploadToGCS = async (files: File[], uploads: any[]) => {
  await Promise.all(
    uploads.map(async (u, i) => {
      const contentType =
        files[i].type || "application/octet-stream";

      const res = await fetch(u.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: files[i],
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("❌ GCS upload failed:", err);
        throw new Error("Upload failed");
      }
    })
  );
};

export const startUploadFlow = async (files: File[], notes: string, jobId: number) => {
  const batchRes = await apiClient.post<CreateBatchResponse>("/upload/create-batch", {
    total_files: files.length,
    job_id: jobId,
    notes,
  });

  const batchId = batchRes.data.batch_id;

  const uploadRes = await apiClient.post<UploadUrlsResponse>("/upload/get-upload-urls", {
    batch_id: batchId,
    files: files.map((f) => ({
      file_name: f.name,
      content_type: f.type,
    })),
  });

  return {
    batchId,
    uploads: uploadRes.data.uploads,
  };
};

export const fetchJobDetails = async (jobId: string) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await apiClient.get(API_ENDPOINTS.JOB_BY_ID(jobId), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: unknown) {
    if (typeof error === "object" && error !== null && "response" in error) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };

      const errorMessage = err.response?.data?.message || `Failed to fetch job details: ${err.message ?? "Unknown error"}`;
      throw new Error(errorMessage);
    }
    if (error instanceof Error) {
      throw new Error(`Failed to fetch job details: ${error.message}`);
    }
    throw new Error("Failed to fetch job details: Unknown error");
  }
};
